import { OutboundCallDetails } from './OutboundCallDetails';
import { formatVoiceProvider } from '@/lib/voice-provider';
import { formatInterviewQuality } from '@/lib/interview-quality';
import { type PanelSpan } from '@/components/shared';
import {
  formatBoolean,
  formatDateTime,
  formatJson,
  formatNullable,
  formatShortId,
  formatStatus,
} from '../format';
import type { BackofficeInterviewDetail } from '../types';
import { InterviewDetailPanel } from './InterviewDetailPanelGrid';
import { DetailRow } from './DetailRow';

export interface InterviewDetailsPanelProps {
  interview: BackofficeInterviewDetail;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
}

export function InterviewDetailsPanel({
  interview,
  panelId,
  currentSpan,
}: InterviewDetailsPanelProps) {
  const recordingLayout =
    typeof interview.recordingMedia?.metadata?.recordingChannelLayout === 'string'
      ? interview.recordingMedia.metadata.recordingChannelLayout
      : null;
  const recordingMediaLabel = interview.recordingMedia
    ? [interview.recordingMedia.contentType, recordingLayout].filter(Boolean).join(' - ')
    : null;

  const detailItems = [
    ...(interview.outboundCall
      ? [
          {
            id: 'outbound-call',
            label: 'Outbound call',
            content: <OutboundCallDetails call={interview.outboundCall} />,
          },
        ]
      : []),
    {
      id: 'id',
      label: 'ID',
      content: (
        <DetailRow
          key="id"
          label="ID"
          value={formatShortId(interview.id)}
          copyValue={interview.id}
          copyLabel="Copy interview ID"
        />
      ),
    },
    {
      id: 'study',
      label: 'Study',
      content: (
        <DetailRow
          key="study"
          label="Study"
          value={interview.study.title}
          href={`/projects/${interview.study.id}`}
        />
      ),
    },
    {
      id: 'study-id',
      label: 'Study ID',
      content: (
        <DetailRow
          key="study-id"
          label="Study ID"
          value={formatShortId(interview.study.id)}
          copyValue={interview.study.id}
          copyLabel="Copy study ID"
        />
      ),
    },
    {
      id: 'organization-id',
      label: 'Organization ID',
      content: (
        <DetailRow
          key="organization-id"
          label="Organization ID"
          value={formatShortId(interview.organizationId)}
          copyValue={interview.organizationId}
          copyLabel="Copy organization ID"
        />
      ),
    },
    {
      id: 'config-version',
      label: 'Config version',
      content: (
        <DetailRow
          key="config-version"
          label="Config version"
          value={String(interview.configVersion)}
        />
      ),
    },
    {
      id: 'call-id',
      label: 'Call ID',
      content: <DetailRow key="call-id" label="Call ID" value={formatNullable(interview.callId)} />,
    },
    {
      id: 'voice',
      label: 'Voice',
      content: <DetailRow key="voice" label="Voice" value={formatNullable(interview.voice)} />,
    },
    {
      id: 'voice-assistant',
      label: 'Voice assistant',
      content: (
        <DetailRow
          key="voice-assistant"
          label="Voice assistant"
          value={formatVoiceProvider(interview.conversationProvider)}
        />
      ),
    },
    {
      id: 'startup-replaced-by',
      label: 'Replaced by',
      content: (
        <DetailRow
          key="startup-replaced-by"
          label="Replaced by"
          value={formatNullable(interview.startupReplacedBy)}
          {...(interview.startupReplacedBy
            ? {
                href: `/interviews/${interview.startupReplacedBy}`,
                linkLabel: 'Open replacement interview',
                copyValue: interview.startupReplacedBy,
                copyLabel: 'Copy replacement interview ID',
              }
            : {})}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      content: <DetailRow key="status" label="Status" value={formatStatus(interview.status)} />,
    },
    {
      id: 'quality-status',
      label: 'Quality status',
      content: (
        <DetailRow
          key="quality-status"
          label="Quality status"
          value={formatInterviewQuality(interview)}
        />
      ),
    },
    {
      id: 'duration-minutes',
      label: 'Duration minutes',
      content: (
        <DetailRow
          key="duration-minutes"
          label="Duration minutes"
          value={formatNullable(interview.durationMinutes)}
        />
      ),
    },
    {
      id: 'duration-seconds',
      label: 'Duration seconds',
      content: (
        <DetailRow
          key="duration-seconds"
          label="Duration seconds"
          value={formatNullable(interview.durationSeconds)}
        />
      ),
    },
    {
      id: 'quality-level',
      label: 'Quality level',
      content: (
        <DetailRow
          key="quality-level"
          label="Quality level"
          value={formatNullable(interview.successEvaluation)}
        />
      ),
    },
    {
      id: 'included-in-analysis',
      label: 'Included in analysis',
      content: (
        <DetailRow
          key="included-in-analysis"
          label="Included in analysis"
          value={formatBoolean(interview.includedInAnalysis)}
        />
      ),
    },
    {
      id: 'usage-finalized',
      label: 'Usage finalized',
      content: (
        <DetailRow
          key="usage-finalized"
          label="Usage finalized"
          value={interview.usageFinalized ? 'Yes' : 'No'}
        />
      ),
    },
    {
      id: 'token-usage',
      label: 'Token usage',
      content: (
        <DetailRow key="token-usage" label="Token usage" value={formatJson(interview.tokenUsage)} />
      ),
    },
    {
      id: 'duration-warning',
      label: 'Duration warning',
      content: (
        <DetailRow
          key="duration-warning"
          label="Duration warning"
          value={interview.durationWarning ? 'Yes' : 'No'}
        />
      ),
    },
    {
      id: 'warning-at',
      label: 'Warning at',
      content: (
        <DetailRow
          key="warning-at"
          label="Warning at"
          value={formatDateTime(interview.warningAt)}
        />
      ),
    },
    {
      id: 'error-message',
      label: 'Error message',
      content: (
        <DetailRow
          key="error-message"
          label="Error message"
          value={formatNullable(interview.errorMessage)}
        />
      ),
    },
    {
      id: 'ended-reason',
      label: 'Ended reason',
      content: (
        <DetailRow
          key="ended-reason"
          label="Ended reason"
          value={formatNullable(interview.endedReason)}
        />
      ),
    },
    {
      id: 'started',
      label: 'Started',
      content: (
        <DetailRow key="started" label="Started" value={formatDateTime(interview.startedAt)} />
      ),
    },
    {
      id: 'connected',
      label: 'Connected',
      content: (
        <DetailRow
          key="connected"
          label="Connected"
          value={formatDateTime(interview.connectedAt)}
        />
      ),
    },
    {
      id: 'ended',
      label: 'Ended',
      content: <DetailRow key="ended" label="Ended" value={formatDateTime(interview.endedAt)} />,
    },
    {
      id: 'created',
      label: 'Created',
      content: (
        <DetailRow key="created" label="Created" value={formatDateTime(interview.createdAt)} />
      ),
    },
    {
      id: 'updated',
      label: 'Updated',
      content: (
        <DetailRow key="updated" label="Updated" value={formatDateTime(interview.updatedAt)} />
      ),
    },
    {
      id: 'recording-url',
      label: 'Recording URL',
      content: (
        <DetailRow
          key="recording-url"
          label="Recording URL"
          value={
            interview.recordingMedia
              ? 'Open stored recording'
              : formatNullable(interview.recordingUrl)
          }
          {...(interview.recordingMedia && interview.recordingUrl
            ? {
                href: interview.recordingUrl,
                linkLabel: 'Open stored recording',
              }
            : {})}
        />
      ),
    },
    {
      id: 'recording-media',
      label: 'Recording media',
      content: (
        <DetailRow
          key="recording-media"
          label="Recording media"
          value={formatNullable(recordingMediaLabel)}
        />
      ),
    },
    {
      id: 'conversation-cost',
      label: 'Conversation cost (USD)',
      content: (
        <DetailRow
          key="conversation-cost"
          label="Conversation cost (USD)"
          value={formatNullable(interview.conversationCost)}
        />
      ),
    },
  ];

  return (
    <InterviewDetailPanel
      title="Interview details"
      span="2x5"
      panelId={panelId}
      currentSpan={currentSpan}
      settingsItems={detailItems}
    />
  );
}
